import { Button, ButtonRole, ButtonTargetKind } from "@components/Button";
import { Icon, IconSize, IconDisplay } from "@components/Icon";
import { DonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import {
  DonateFormType,
  DONATE_FORM_ERROR,
  DONATE_STRIPE_ERROR,
} from "@components/donate/DonateV3/types";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { DonationFlowPaymentOption } from "@every.org/common/src/entity/types";

import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { horizontalStackCss, spacing } from "src/theme/spacing";

const BackButtonContainer = styled.div`
  padding: 0;
  margin-bottom: ${spacing.l};

  ${cssForMediaSize({
    min: MediaSize.X_LARGE,
    css: css`
      padding: 20px;
    `,
  })};
`;

interface GoBackButtonProps {
  form: UseFormReturn<DonateFormType>;
  formContext: DonateFormContext;
  /**
   * The payment option the donor is currently in. Required so the analytics
   * tname names the real flow; a default here would silently re-attribute a
   * new call site's Back clicks to whichever option we picked.
   */
  paymentOption: DonationFlowPaymentOption;
}

export function GoBackButton({
  form,
  formContext,
  paymentOption,
}: GoBackButtonProps) {
  const {
    formState: { errors },
  } = form;

  const navigate = useNavigate();

  const clearFormError = useCallback(async () => {
    if (errors.DONATE_FORM_ERROR || errors.DONATE_STRIPE_ERROR) {
      form.clearErrors(DONATE_FORM_ERROR);
      form.clearErrors(DONATE_STRIPE_ERROR);
      await formContext.refreshNonce();
    }
  }, [form, formContext, errors]);

  const goBack = useCallback(() => {
    clearFormError();
    navigate(-1);
  }, [navigate, clearFormError]);

  return (
    <BackButtonContainer>
      <Button
        role={ButtonRole.TEXT_ONLY}
        onClick={{ kind: ButtonTargetKind.FUNCTION, action: goBack }}
        data-tname={`donateClickBack__${paymentOption}`}
        contentCss={horizontalStackCss.xxs}
      >
        <Icon
          iconImport={() => import("@components/Icon/icons/ArrowBackIcon")}
          size={IconSize.MEDIUM}
          display={IconDisplay.ACCENT}
        />
        <span>Back</span>
      </Button>
    </BackButtonContainer>
  );
}
